// Industry-standard "one match-related commitment at a time" cleanup.
// Fired whenever the user commits to a match-bearing slot (joins an
// instance, starts a solo sandbox, or gets paired in a ranked queue).
// Cancels every other outgoing public/private match listing they have
// open and auto-declines every still-pending incoming invite so other
// players don't waste a click on a slot the user can no longer honor.
//
// Best-effort and idempotent: failures are warned but never thrown.
// the calling match-join flow must never be blocked by cleanup.

import { listInstances, abandonInstance } from './instancesApi';
import { declineDraftEvent } from './draftApi';
import { fetchConversations, fetchMessages, claimChatMessage } from './chatApi';
import { declineMatchInvite } from '../friendsApi';

// Match the freshness window used by InstanceQueueIndicator so we
// only act on invites the queue UI would still treat as live.
const INVITE_FRESHNESS_MS = 6 * 60 * 60 * 1000;

async function cancelOutgoingInstances(profileId, excludeInstanceId) {
  let all;
  try {
    all = await listInstances();
  } catch {
    return;
  }
  const targets = (all || []).filter((i) => {
    if (i.id === excludeInstanceId) return false;
    if (i.phase !== 'gathering') return false;
    const me = (i.participants || []).find((p) => p.userId === profileId);
    return me?.role === 'creator';
  });
  await Promise.allSettled(targets.map(async (inst) => {
    try {
      // Server-side cascade: /abandon aborts any linked draft_event
      // (escrow refunds included), so one call settles the lobby.
      await abandonInstance(inst.id);
    } catch (err) {
      console.warn('[matchEntryCleanup] abandon failed:', err?.message || err);
    }
  }));
}

async function declineIncomingInvites(profileId) {
  let convos;
  try {
    convos = await fetchConversations();
  } catch {
    return;
  }
  const cutoff = Date.now() - INVITE_FRESHNESS_MS;
  const pending = [];
  await Promise.all((convos || []).map(async (convo) => {
    const partnerId = convo.friendId;
    if (!partnerId) return;
    let msgs;
    try {
      msgs = await fetchMessages(partnerId, { limit: 20 });
    } catch {
      return;
    }
    for (const m of msgs || []) {
      const isMatchInvite = m.type === 'match-invite' && m.metadata?.instanceId;
      const isDraftInvite = m.type === 'draft-invite' && (m.metadata?.eventId || m.metadata?.instanceId);
      if (!isMatchInvite && !isDraftInvite) continue;
      if (m.claimed) continue;
      if (m.senderId === profileId) continue;
      // Resume invites point at a paused match the user is meant to
      // rejoin. Keep these alive so the user can still resume.
      if (m.metadata?.resume) continue;
      const created = m.createdAt ? new Date(m.createdAt).getTime() : 0;
      if (!created || created < cutoff) continue;
      pending.push(m);
    }
  }));
  await Promise.allSettled(pending.map(async (m) => {
    try {
      if (m.type === 'draft-invite') {
        const eventId = m.metadata?.eventId;
        if (eventId) {
          await declineDraftEvent(eventId).catch((err) => {
            console.warn('[matchEntryCleanup] draft decline failed:', err?.message || err);
          });
        }
      } else {
        try { declineMatchInvite(m.senderId); } catch {}
      }
      await claimChatMessage(m.id);
    } catch (err) {
      console.warn('[matchEntryCleanup] invite decline failed:', err?.message || err);
    }
  }));
}

export async function cleanupOnMatchEntry({ profile, excludeInstanceId = null } = {}) {
  const profileId = profile?.id;
  if (!profileId) return;
  await Promise.allSettled([
    cancelOutgoingInstances(profileId, excludeInstanceId),
    declineIncomingInvites(profileId),
  ]);
}
